import { z } from 'zod'
import { isAddress } from 'viem'
import { networkTypeList } from '@provex/eip712-utils/network-types'
import { pricingSourcesList, pricingSources, exchangesUnderSources, actualPricingSources } from './price-source-config.js'
import { PriceSourceSchema, ProjectSocialLinksSchema, ProjectTagsSchema, RequestWithSignatureSchema, bigintString } from './api.js'
import { derivesPerIndexAddresses } from './network.js'

/** Publication mode for per-index-deriving networks (bip32, cardano, monero, solana). */
export const PublicationModeSchema = z.enum(['backend-live', 'published-pool'])
export type PublicationMode = z.infer<typeof PublicationModeSchema>

/** Whether the campaign issues deposit indices from the on-chain registry or the backend counter. */
export const IncrementModeSchema = z.enum(['onchain', 'offchain'])
export type IncrementMode = z.infer<typeof IncrementModeSchema>

/** Genesis-immutable fields: set at campaign creation and never changed. */
export const CampaignConfigGenesisSchema = z.object({
  incrementMode: IncrementModeSchema,
  registryAddress: z.string().nullable(),
  /**
   * The on-chain project key: keccak256(abi.encode(creatorAddress, projectName)).
   * Non-null only when incrementMode === 'onchain'. Cached here so the backend
   * never recomputes it from a live registry call on the hot path. Null when
   * incrementMode === 'offchain'.
   */
  registryProjectKey: z.string().nullable(),
})
export type CampaignConfigGenesis = z.infer<typeof CampaignConfigGenesisSchema>

/**
 * Resolves the effective publication mode for a network at creation time.
 * - Shared-address networks (evm, tvm, memo) return null — publication modes do not apply.
 * - Solana is always forced to 'published-pool'.
 * - Other per-index-deriving networks use the configured value, defaulting to 'published-pool'.
 */
export const resolvePublicationMode = (params: {
  networkType: string
  configured: PublicationMode | undefined
}): PublicationMode | null => {
  if (!derivesPerIndexAddresses(params.networkType)) return null
  if (params.networkType === 'solana') return 'published-pool'
  return params.configured ?? 'published-pool'
}

/** The network type values supported across the system (evm, bip32, memo, ...). */
export const networkTypeSchema = z.enum(networkTypeList)

/** The pricing source values (stable, ccxt, dex). */
export const pricingSourceSchema = z.enum(pricingSourcesList)

/**
 * A single typed predicate over candidate networks/tokens. Discriminated on
 * `kind` so an unrecognised filter is a parse error, never a silent no-operation.
 */
export const DimensionFilterSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('networkType'), networkTypes: z.array(networkTypeSchema) }),
  z.object({
    kind: z.literal('chainId'),
    chains: z.array(z.object({ networkType: networkTypeSchema, chainId: z.number().int() })),
  }),
  z.object({ kind: z.literal('tag'), tags: z.array(z.string()) }),
  z.object({ kind: z.literal('priceSource'), sources: z.array(pricingSourceSchema) }),
  z.object({ kind: z.literal('symbol'), symbols: z.array(z.string()) }),
])
export type DimensionFilter = z.infer<typeof DimensionFilterSchema>

/** A composed group of filters: `all` = intersection, `any` = union; `not` negates the group. */
export const DimensionFilterGroupSchema = z.object({
  mode: z.enum(['all', 'any']),
  not: z.boolean().optional(),
  filters: z.array(DimensionFilterSchema),
})
export type DimensionFilterGroup = z.infer<typeof DimensionFilterGroupSchema>

/** The full filter set that shaped a config; descriptive provenance, not executable at runtime. */
export const DimensionFilterSetSchema = z.object({
  networkFilters: DimensionFilterGroupSchema,
  tokenFilters: DimensionFilterGroupSchema,
})
export type DimensionFilterSet = z.infer<typeof DimensionFilterSetSchema>

/**
 * A project_network row's DEFINITION fields only. Runtime cursors
 * (lastFinalizedBlock, lowestBlock, highestBlock, nextIndex) are excluded — they
 * are re-initialised on import, never carried.
 */
export const CampaignConfigNetworkSchema = z.object({
  networkType: networkTypeSchema,
  chainId: z.number().int(),
  networkName: z.string(),
  rootAddress: z.string(),
  lowerRange: z.number().int().nullable(),
  upperRange: z.number().int().nullable(),
  rpcUrls: z.string().nullable(),
  disabled: z.boolean(),
  publicationMode: PublicationModeSchema.optional(),
})
export type CampaignConfigNetwork = z.infer<typeof CampaignConfigNetworkSchema>

/** A price_source definition without projectId (the import target supplies projectId). */
export const CampaignConfigPriceSourceSchema = PriceSourceSchema.omit({ projectId: true })
export type CampaignConfigPriceSource = z.infer<typeof CampaignConfigPriceSourceSchema>

/** Campaign identity + terms (the seven Phase 1 identity columns + description/website/logo). */
export const CampaignConfigIdentitySchema = z.object({
  projectId: z.string().min(1),
  projectName: z.string(),
  projectDescription: z.string(),
  projectTagline: z.string().nullable(),
  projectWebsite: z.string().nullable(),
  projectLogo: z.string().nullable(),
  projectBanner: z.string().nullable(),
  projectAccentColor: z.string().nullable(),
  projectAccentColorVariant: z.string().nullable(),
  projectDisclaimerShort: z.string().nullable(),
  projectDisclaimerLong: z.string(),
  projectSocialLinks: ProjectSocialLinksSchema,
  projectTags: ProjectTagsSchema,
})
export type CampaignConfigIdentity = z.infer<typeof CampaignConfigIdentitySchema>

/**
 * Campaign window. Times are decimal-string seconds (JavaScript Object Notation
 * native, round-trip-safe). Each field is constrained to a non-negative whole number
 * of seconds at the structural-parsing boundary, so {@link validateCampaignConfig}
 * can convert these strings to bigints without risk of a syntax error.
 */
const secondsString = z.string().regex(/^\d+$/, 'must be a non-negative whole number of seconds')
export const CampaignConfigScheduleSchema = z.object({
  startTime: secondsString,
  inflectionTime: secondsString,
  endTime: secondsString.nullable(),
})
export type CampaignConfigSchedule = z.infer<typeof CampaignConfigScheduleSchema>

/** Decay / bonus / points configuration. Bigint fields are decimal strings; cutoffs are numbers. */
export const CampaignConfigCurveSchema = z.object({
  decayRateSingleStep: z.string(),
  decaySingleStepDelaySeconds: z.string(),
  volumeBonusRate: z.string(),
  volumeBonusCutoff: z.number().int(),
  flatBonusRate: z.string(),
  flatBonusCutoff: z.number().int(),
  usdToPointBaseRate: z.string(),
  referralRate: z.string().nullable(),
  countEarlySacrifices: z.boolean(),
  useInflectionStepOne: z.boolean(),
  priceValidFromTimestamp: z.string().nullable(),
})
export type CampaignConfigCurve = z.infer<typeof CampaignConfigCurveSchema>

/**
 * The artifact format version. Bump when the shape changes incompatibly; a parsed
 * config whose version is not exactly this is rejected (a later stage adds a
 * friendly "exported by a newer composer" message and any migration).
 */
export const CAMPAIGN_CONFIG_FORMAT_VERSION = 1 as const

/**
 * The complete, portable definition of one campaign — everything needed to
 * recreate the campaign's intent on a fresh database, minus all collected/derived
 * runtime state. The unit that export produces, import consumes, a preset is a
 * saved instance of, and clone is an in-memory copy of. Every field is JavaScript
 * Object Notation native so the artifact round-trips without bespoke bigint handling.
 */
export const CampaignConfigSchema = z.object({
  formatVersion: z.literal(CAMPAIGN_CONFIG_FORMAT_VERSION),
  identity: CampaignConfigIdentitySchema,
  schedule: CampaignConfigScheduleSchema,
  curve: CampaignConfigCurveSchema,
  networks: z.array(CampaignConfigNetworkSchema),
  priceSources: z.array(CampaignConfigPriceSourceSchema),
  cexAddresses: z.array(z.string()),
  dimensions: DimensionFilterSetSchema.optional(),
  genesis: CampaignConfigGenesisSchema.optional(),
})
export type CampaignConfig = z.infer<typeof CampaignConfigSchema>

/** Stable string comparator (database row order is not guaranteed, so we sort). */
const byString = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0)

/** Stable numeric comparator (chain identifiers must order numerically, not lexically). */
const byNumber = (a: number, b: number): number => a - b

/**
 * Orders networks by network type first, then by numeric chain identifier. Sorting the
 * chain identifier numerically (not as a string) keeps, for example, chain 56 before
 * chain 369.
 */
const compareNetworks = (a: CampaignConfigNetwork, b: CampaignConfigNetwork): number =>
  byString(a.networkType, b.networkType) || byNumber(a.chainId, b.chainId)

/**
 * Orders price sources by network type, then numeric chain identifier, then token
 * address — a total order so two configs differing only by row order compare equal.
 */
const comparePriceSources = (a: CampaignConfigPriceSource, b: CampaignConfigPriceSource): number =>
  byString(a.networkType, b.networkType) ||
  byNumber(a.chainId, b.chainId) ||
  byString(a.tokenAddress, b.tokenAddress)

const socialLinkKey = (link: { platform: string; url: string }): string => `${link.platform}:${link.url}`

/**
 * Returns a deep copy of the config in canonical form: arrays whose order is not
 * semantically meaningful are sorted so two configs that differ only by ordering
 * compare equal. Pure — never mutates its input. This is what makes the round-trip
 * equality lossless rather than flaky.
 */
export const normalizeCampaignConfig = (config: CampaignConfig): CampaignConfig => ({
  ...config,
  identity: {
    ...config.identity,
    projectTags: [...config.identity.projectTags].sort(byString),
    projectSocialLinks: [...config.identity.projectSocialLinks].sort((a, b) =>
      byString(socialLinkKey(a), socialLinkKey(b)),
    ),
  },
  networks: [...config.networks].sort(compareNetworks),
  priceSources: [...config.priceSources].sort(comparePriceSources),
  cexAddresses: [...config.cexAddresses].sort(byString),
})

/** Serialises a config to a JavaScript Object Notation string. Pair with {@link parseCampaignConfig}. */
export const serializeCampaignConfig = (config: CampaignConfig): string => JSON.stringify(config)

/**
 * The deterministic canonical serialization of a config — normalised (so
 * order-insensitive arrays are sorted) then JSON-serialised. Because
 * {@link CampaignConfigSchema} parsing emits keys in schema order, this string is
 * byte-identical on the browser that signs an import and the server that recovers
 * it, so it is the safe payload to bind a signature to (no separate hash needed).
 */
export const canonicalCampaignConfigString = (config: CampaignConfig): string =>
  serializeCampaignConfig(normalizeCampaignConfig(config))

/**
 * Parses and structurally validates a JavaScript Object Notation string into a
 * CampaignConfig. Throws a Zod error if the text is not valid JavaScript Object
 * Notation or does not match the schema.
 */
export const parseCampaignConfig = (text: string): CampaignConfig =>
  CampaignConfigSchema.parse(JSON.parse(text))

/**
 * The wire shape of a signed campaign import: the standard signature + serialized
 * inputs envelope, extended with the full artifact. The signature is recovered over
 * {@link canonicalCampaignConfigString}(config) + inputs.timestamp by the import
 * endpoint. Defined here (not in api.ts) to avoid a circular import — api.ts does not
 * know about CampaignConfig, but campaign-config.ts already depends on api.ts.
 */
export const CampaignImportRequestSchema = RequestWithSignatureSchema(
  z.object({ timestamp: bigintString }),
).extend({
  config: CampaignConfigSchema,
})
export type CampaignImportRequest = z.infer<typeof CampaignImportRequestSchema>

export type CampaignConfigValidationError = { path: string; message: string }
export type CampaignConfigValidationResult =
  | { valid: true }
  | { valid: false; errors: CampaignConfigValidationError[] }

/**
 * Semantic, cross-field validation that structural parsing cannot express: schedule
 * ordering, within-artifact referential integrity (a token must price on a network the
 * campaign includes), price-source coherence, and definition uniqueness. Pure — the
 * single source of truth reused by the import endpoint, the composer pre-submit check,
 * and the round-trip/clone tests.
 */
export const validateCampaignConfig = (config: CampaignConfig): CampaignConfigValidationResult => {
  const errors: CampaignConfigValidationError[] = []

  const startTime = BigInt(config.schedule.startTime)
  const inflectionTime = BigInt(config.schedule.inflectionTime)
  if (inflectionTime < startTime) {
    errors.push({ path: 'schedule.inflectionTime', message: 'inflection time must not precede the start time' })
  }
  if (config.schedule.endTime !== null && BigInt(config.schedule.endTime) < inflectionTime) {
    errors.push({ path: 'schedule.endTime', message: 'end time must not precede the inflection time' })
  }

  // Genesis cross-field validation (D-S3-1, D-S3-2)
  const genesis = config.genesis
  if (genesis !== undefined) {
    if (genesis.incrementMode === 'onchain') {
      if (
        genesis.registryAddress === null ||
        genesis.registryAddress.trim() === '' ||
        !isAddress(genesis.registryAddress)
      ) {
        errors.push({
          path: 'genesis.registryAddress',
          message: 'onchain increment mode requires a valid EVM registry address',
        })
      }
      // registryProjectKey must be a 32-byte hex string (keccak256 output).
      if (
        genesis.registryProjectKey === null ||
        !/^0x[0-9a-fA-F]{64}$/.test(genesis.registryProjectKey)
      ) {
        errors.push({
          path: 'genesis.registryProjectKey',
          message: 'onchain increment mode requires a valid 32-byte hex registry project key',
        })
      }
    } else {
      // offchain: registryAddress must not be set
      if (genesis.registryAddress !== null) {
        errors.push({
          path: 'genesis.registryAddress',
          message: 'registryAddress must be null when incrementMode is offchain',
        })
      }
      // offchain: registryProjectKey must not be set
      if (genesis.registryProjectKey !== null) {
        errors.push({
          path: 'genesis.registryProjectKey',
          message: 'registryProjectKey must be null when incrementMode is offchain',
        })
      }
    }
  }

  const networkKeys = new Set<string>()
  config.networks.forEach((network, index) => {
    const key = `${network.networkType}:${network.chainId}`
    if (networkKeys.has(key)) {
      errors.push({ path: `networks[${index}]`, message: `duplicate network ${key}` })
    }
    networkKeys.add(key)
    if (network.rootAddress.trim() === '') {
      errors.push({
        path: `networks[${index}].rootAddress`,
        message: `network ${key} requires a root address — deposit addresses derive from it`,
      })
    }
    // Publication mode: only valid on per-index-deriving networks; solana must not be backend-live.
    if (network.publicationMode !== undefined) {
      if (!derivesPerIndexAddresses(network.networkType)) {
        errors.push({
          path: `networks[${index}].publicationMode`,
          message: `network ${key} is a shared-address network (${network.networkType}) and does not support publicationMode`,
        })
      } else if (network.networkType === 'solana' && network.publicationMode === 'backend-live') {
        errors.push({
          path: `networks[${index}].publicationMode`,
          message: `solana networks are always published-pool — backend-live is not allowed`,
        })
      }
    }
  })

  const priceSourceKeys = new Set<string>()
  config.priceSources.forEach((priceSource, index) => {
    const networkLookupKey = `${priceSource.networkType}:${priceSource.chainId}`
    if (!networkKeys.has(networkLookupKey)) {
      errors.push({ path: `priceSources[${index}]`, message: `price source references network ${networkLookupKey} which is not in the campaign` })
    }
    const dedupeKey = `${networkLookupKey}:${priceSource.tokenAddress}`
    if (priceSourceKeys.has(dedupeKey)) {
      errors.push({ path: `priceSources[${index}]`, message: `duplicate price source ${dedupeKey}` })
    }
    priceSourceKeys.add(dedupeKey)
    // The stored price_source.source domain is the full set of exchange-source keys
    // (stable, ccxt, dex) PLUS the special-case markers 'zero' (explicitly ignored
    // token) and 'pending' (awaiting a pricing mechanism). The exporter projects all
    // live price_source rows verbatim, so the importer must accept every value the
    // exporter can emit — validating against only the actual pricing sources would
    // reject legitimately exported 'zero'/'pending' rows and break the round-trip.
    if (!(priceSource.source in exchangesUnderSources)) {
      errors.push({ path: `priceSources[${index}].source`, message: `unknown pricing source ${priceSource.source}` })
    }
    // A market is only meaningful for the actual non-stable pricing sources (ccxt, dex).
    // The 'stable' source and the 'zero'/'pending' markers carry no market.
    const requiresMarket = priceSource.source in actualPricingSources && priceSource.source !== pricingSources.stable
    if (requiresMarket && priceSource.market.trim() === '') {
      errors.push({ path: `priceSources[${index}].market`, message: 'a non-stable price source requires a market' })
    }
  })

  if (errors.length > 0) {
    return { valid: false, errors }
  }
  return { valid: true }
}
